import React from 'react'
import {Select} from 'react-select-virtualized'
import Typography from '@mui/material/Typography'
import TextField from '@mui/material/TextField'
import Paper from '@mui/material/Paper'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import CancelIcon from '@mui/icons-material/Cancel'
import {emphasize} from '@mui/material/styles'
import {makeStyles} from '@oc/styles'
import {ValidationResult, ValidationState} from './Validation'
import {Tooltip} from '@mui/material'
import {cx} from '@emotion/css'

const useStyles = makeStyles(theme => ({
    root: {
        flexGrow: 1,
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    selectRoot: {
        flex: '1 1 0',
        width: '100%',
        '& .fast-option': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
        }
    },
    input: {
        display: 'flex',
        padding: theme.spacing(1, 0)
    },
    inputMulti: {
        display: 'flex',
        padding: theme.spacing(1, 0),
        height: 'auto',
        alignItems: 'center'
    },
    valueContainer: {
        overflow: 'hidden',
        flex: 1,
        display: 'flex',
        flexWrap: 'wrap'
    },
    chip: {
        margin: theme.spacing(0.5, 0.25)
    },
    chipFocused: {
        backgroundColor: emphasize(
            theme.palette.mode === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
            0.08
        )
    },
    noOptionsMessage: {
        padding: theme.spacing(1, 2)
    },
    singleValue: {
        fontSize: 16,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        maxWidth: 'calc(100% - 10px)'
    },
    placeholder: {
        position: 'absolute',
        left: 2,
        fontSize: 16
    },
    paper: {
        position: 'absolute',
        zIndex: 100,
        marginTop: theme.spacing(1),
        left: 0,
        right: 0
    },
    divider: {
        height: theme.spacing(2)
    },
    helper: {
        flex: '1 1 100%'
    }
}))

function NoOptionsMessage(props: any) {
    return (
        <Typography color="textSecondary" className={props.selectProps.classes.noOptionsMessage} {...props.innerProps}>
            {props.children}
        </Typography>
    )
}

const inputComponent = React.forwardRef<HTMLDivElement, any>(function InputComponent(props, ref) {
    return <div ref={ref} {...props} />
})

function Control(props: any) {
    return (
        <TextField
            fullWidth
            {...props.selectProps.textFieldProps}
            slotProps={{
                input: {
                    inputComponent,
                    inputProps: {
                        className: (props.isMulti as boolean)
                            ? props.selectProps.classes.inputMulti
                            : props.selectProps.classes.input,
                        ref: props.innerRef,
                        children: props.children,
                        ...props.innerProps
                    }
                }
            }}
        />
    )
}

function Option(props: any) {
    return (
        <MenuItem
            buttonRef={props.innerRef}
            selected={props.isFocused}
            component="div"
            style={{
                fontWeight: props.isSelected ? 500 : 400
            }}
            {...props.innerProps}
        >
            {props.children}
        </MenuItem>
    )
}

function Placeholder(props: any) {
    return (
        <Typography color="textSecondary" className={props.selectProps.classes.placeholder} {...props.innerProps}>
            {props.children}
        </Typography>
    )
}

function SingleValue(props: any) {
    return (
        <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
            {props.children}
        </Typography>
    )
}

function ValueContainer(props: any) {
    return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>
}

function MultiValue(props: any) {
    return (
        <Chip
            tabIndex={-1}
            label={props.children}
            className={cx(props.selectProps.classes.chip, {
                [props.selectProps.classes.chipFocused]: props.isFocused
            })}
            onDelete={props.removeProps.onClick}
            deleteIcon={<CancelIcon {...props.removeProps} />}
        />
    )
}

function Menu(props: any) {
    return (
        <Paper square className={props.selectProps.classes.paper} {...props.innerProps}>
            {props.children}
        </Paper>
    )
}

const components = {
    Control,
    Menu,
    MultiValue,
    NoOptionsMessage,
    Option,
    Placeholder,
    SingleValue,
    ValueContainer
}

interface Suggestion {
    label: string
    value: any
}

interface AutocompleteSelectPropTypes<IsMulti extends boolean = false> {
    classes?: Partial<ReturnType<typeof useStyles>['classes']>
    placeholder: string
    isClearable?: boolean
    suggestions: Suggestion[]
    label: string
    value: any
    onChange: (data: any) => void
    helper?: string
    disabled?: boolean
    onFocus?: () => void
    multi?: IsMulti
    rootId?: string
    onMenuOpen?: () => void
    onMenuClose?: () => void
    showTooltip?: boolean
    validator?: (value?: IsMulti extends true ? Suggestion[] : Suggestion) => ValidationResult
    multiSearch?: boolean
}

export function AutocompleteSelect<IsMulti extends boolean = false>(props: AutocompleteSelectPropTypes<IsMulti>) {
    const {classes: defaultClasses, theme} = useStyles()
    const classes = {
        ...defaultClasses,
        ...props.classes
    }
    const selectStyles = {
        input: (base: any) => ({
            ...base,
            color: theme.palette.text.primary,
            '& input': {
                font: 'inherit'
            }
        })
    }

    const validationResult: ValidationResult = props.validator
        ? props.validator(props.value)
        : {
              message: '',
              sanitizedValue: props.value,
              error: ValidationState.OK
          }
    const isError = validationResult.error === ValidationState.Error

    const customFilter = (option: any, searchText: string) =>
        searchText
            .toLowerCase()
            .split(' ')
            .every(word => option.label.toLowerCase().includes(word))

    return (
        <>
            <Tooltip title={props.showTooltip ? props.value?.label || '' : ''}>
                <div className={classes.root}>
                    <Select
                        className={classes.selectRoot}
                        classes={classes}
                        styles={selectStyles}
                        options={props.suggestions}
                        components={components}
                        value={props.value}
                        isDisabled={props.disabled || false}
                        onFocus={props.onFocus}
                        textFieldProps={{
                            label: props.label,
                            InputLabelProps: {
                                shrink: true,
                                error: isError
                            },
                            id: props.rootId,
                            error: isError
                        }}
                        onChange={(e: any) => props.onChange(e)}
                        placeholder={props.placeholder}
                        isClearable={props.isClearable !== undefined ? props.isClearable : true}
                        isMulti={props.multi}
                        closeMenuOnSelect={!props.multi}
                        onMenuOpen={props.onMenuOpen}
                        onMenuClose={props.onMenuClose}
                        validationResult={validationResult}
                        filterOption={props.multiSearch ? customFilter : undefined}
                    />
                </div>
            </Tooltip>
            <Typography
                className={classes.helper}
                color={isError ? 'error' : 'textPrimary'}
                variant="caption"
                id={`${props.rootId}-helper-text`}
            >
                {validationResult.message || props.helper || '\xA0'}
            </Typography>
        </>
    )
}
